import type { OfflineState, Runtime, StreamState } from "@baton/core";
import { useI18n } from "@baton/i18n";
import type { ConnectionState } from "@baton/ui";
import { useSyncExternalStore } from "react";
import { useRestoredAt } from "../realtime/restoreStore.ts";

/**
 * FR-015-090, FR-015-155, `contracts/client-api.md` § State machine and § Offline detection and the
 * write queue, `spec.md` US26 scenario 4: the connection indicator reads the offline machine and the
 * workspace stream together in the contract's one order (offline, then polling's "live updates
 * unavailable", then limited's "live updates paused" for a stream the instance refused, then stopped
 * for a stream closed with no reconnect coming (2026-10-14: a closed stream showed as connected), then reconnecting with its attempt from the first failure, which the stream holds
 * while each retry is in flight, then nothing), the offline banner reads the offline machine, and every
 * mutating control is disabled with its tooltip while offline or behind the restore notice, with
 * cached data kept on screen.
 */
export function useOfflineState(runtime: Runtime): OfflineState {
  return useSyncExternalStore(runtime.offline.subscribe, () => runtime.offline.state);
}

/** The closings that end with the shell they belong to, or hand over to the next workspace's stream, so nothing waits on them. */
const TRANSITIONAL_CLOSES: ReadonlySet<string> = new Set(["unmounted", "workspace-switch"]);

export function connectionOf(offline: OfflineState, stream: StreamState): ConnectionState {
  if (offline === "offline") return { kind: "offline" };
  if (stream.kind === "polling") return { kind: "polling" };
  if (stream.kind === "limited") return { kind: "limited", cause: stream.cause };
  if (stream.kind === "closed" && !TRANSITIONAL_CLOSES.has(stream.reason)) return { kind: "stopped" };
  if (stream.kind === "reconnecting") return { kind: "reconnecting", attempt: stream.attempt };
  return { kind: "connected" };
}

export function useConnectionState(runtime: Runtime, stream: StreamState): ConnectionState {
  return connectionOf(useOfflineState(runtime), stream);
}

export function useQueuedWrites(runtime: Runtime): number {
  return useSyncExternalStore(runtime.writeQueue.subscribe, () => runtime.writeQueue.size);
}

/** What a mutating control reads: disabled while offline or after a restore, with the reason for its tooltip. */
export function useMutationsAllowed(runtime: Runtime): { readonly allowed: boolean; readonly reason: string | null } {
  const { t } = useI18n();
  const offline = useOfflineState(runtime) === "offline";
  const restored = useRestoredAt(runtime) !== null;
  return { allowed: !offline && !restored, reason: restored ? t("shell.restore.blocked") : offline ? t("ui.offline.banner") : null };
}
